/**
 * Rules the waitlist form and the server must agree on.
 *
 * Both import this file, for the same reason `auth.ts` is shared: a form that accepts what
 * the server then refuses teaches people the form is broken.
 */

import { isValidEmail } from './auth.ts'

export const MIN_AGE = 18
export const MAX_NAME_LENGTH = 120

/**
 * ISO 3166-1 alpha-2. Stored as the code, never the name: names differ by language and
 * change with politics, and the code is what any eligibility rule will be written against.
 * The client renders names with `Intl.DisplayNames`.
 */
export const COUNTRY_CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR ' +
  'BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ ' +
  'EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW ' +
  'GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY ' +
  'KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV ' +
  'MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY ' +
  'QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG ' +
  'TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ')

const COUNTRIES = new Set(COUNTRY_CODES)

export interface WaitlistEntry {
  fullName: string
  email: string
  /** `YYYY-MM-DD`, as `<input type="date">` produces it. */
  dateOfBirth: string
  /** ISO 3166-1 alpha-2. */
  country: string
}

/** Whole years between a `YYYY-MM-DD` birth date and `today`, or null if it is not a real date. */
export function ageOn(dateOfBirth: string, today = new Date()): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOfBirth)
  if (!m) return null
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const born = new Date(Date.UTC(y, mo - 1, d))
  // Rejects 2001-02-30 and friends, which `Date` would otherwise roll into March.
  if (born.getUTCFullYear() !== y || born.getUTCMonth() !== mo - 1 || born.getUTCDate() !== d) return null

  let age = today.getUTCFullYear() - y
  const beforeBirthday =
    today.getUTCMonth() < mo - 1 || (today.getUTCMonth() === mo - 1 && today.getUTCDate() < d)
  if (beforeBirthday) age -= 1
  return age
}

/**
 * The first thing wrong with an entry, as a sentence to show next to the form, or null.
 * Every field is required.
 */
export function waitlistProblem(e: WaitlistEntry): string | null {
  const name = e.fullName.trim()
  if (!name) return 'Enter your full name.'
  if (name.length > MAX_NAME_LENGTH) return `Your name must be ${MAX_NAME_LENGTH} characters or fewer.`
  if (!isValidEmail(e.email)) return 'Enter a valid email address.'

  const age = ageOn(e.dateOfBirth)
  if (age === null) return 'Enter a valid date of birth.'
  if (age > 120) return 'Check your date of birth.'
  if (age < MIN_AGE) return `You must be ${MIN_AGE} or over to join.`

  if (!COUNTRIES.has(e.country)) return 'Choose the country you live in.'
  return null
}

/** The personal details on Settings. Email is shown but not edited there. */
export interface ProfileDetails {
  firstName: string
  lastName: string
  /** `YYYY-MM-DD`, or empty when we have never been told. */
  dateOfBirth: string
  /** ISO 3166-1 alpha-2, or empty when we have never been told. */
  country: string
}

/** The first thing wrong with a Settings form, as a sentence, or null. */
export function profileProblem(p: ProfileDetails): string | null {
  const first = p.firstName.trim()
  const last = p.lastName.trim()
  if (!first) return 'Enter your first name.'
  if (!last) return 'Enter your last name.'
  if (first.length + last.length + 1 > MAX_NAME_LENGTH) return `Your name must be ${MAX_NAME_LENGTH} characters or fewer.`

  const age = ageOn(p.dateOfBirth)
  if (age === null) return 'Enter a valid date of birth.'
  if (age > 120) return 'Check your date of birth.'
  if (age < MIN_AGE) return `You must be ${MIN_AGE} or over.`

  if (!COUNTRIES.has(p.country)) return 'Choose the country you live in.'
  return null
}
